import { forwardRef, useContext, useState } from "react";
import {
  Badge,
  Group,
  ActionIcon,
  Avatar,
  TextInput,
  Select,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconX,
  IconSearch,
  IconPrinter,
  IconRefresh,
  IconMailForward,
} from "@tabler/icons-react";
import { openSendCampaignModal } from "./SendCampaignModal";
import { isContentEmpty } from "@congrs/utils";
import { web_createTicketSchema } from "@congrs/schemas";
import { ajv } from "@congrs/resolver";
import { useGetAllCampaigns } from "../../api/campaigns";
import {
  TableActions,
  TableAction,
  EditAction,
  DeleteAction,
} from "../TableActions";
import { InlineEditableTable } from "../InlineEditableTable";
import {
  formatCustomFieldValue,
  BOOLEAN_OPTIONS,
} from "../../utils/customFieldValues";
import { selectableCountries } from "../CountrySelect";
import TicketSwitch from "./TicketSwitch";
import { useGetPeopleTagsBySubtype } from "../../api/tag";
import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import {
  useCreateTicket,
  useDeleteTicket,
  useUpdateTicket,
} from "../../api/tickets";
import { useUpdateUser } from "../../api/user";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";
import { renderBadgePdf, useGetBadgeTemplates } from "../../api/badges";
import { printTicket } from "../../utils/printTicket";
import { useDataTableColumns } from "mantine-datatable";

// Same rule the Create Ticket modal validates against (web_createTicketSchema),
// so inline and modal validation can't drift apart.
const isValidEmail = ajv.compile(web_createTicketSchema.properties.email);

// Field types whose options are the same for every event
const FIXED_OPTIONS = {
  country: selectableCountries,
  boolean: BOOLEAN_OPTIONS,
};

const TicketsTable = forwardRef(
  (
    {
      data,
      pagination,
      sorting,
      isPending,
      filter,
      customColumns,
      columnsData,
    },
    ref
  ) => {
    const { user } = useContext(AuthContext);
    const currentWorkspace = getCurrentWorkspace(user);
    const currentEventId = currentWorkspace.eventId;
    const { data: badgeTemplates } = useGetBadgeTemplates({
      eventId: currentEventId,
    });
    const defaultBadgeId = badgeTemplates?.find((b) => b.isDefault)?.id;

    const handlePrint = async (row) => {
      if (defaultBadgeId) {
        try {
          const { url } = await renderBadgePdf({
            badgeId: defaultBadgeId,
            ticketId: row.id,
          });
          window.open(url, "_blank");
          return;
        } catch (err) {
          printTicket(row);
          return;
        }
      }
      printTicket(row);
    };
    // Keep track of the row being edited to use as param when making mutations calls to update user, ticket, and custom fields
    const [editingRow, setEditingRow] = useState({
      ticketId: null,
      userId: null,
    });

    const { mutateAsync: updateUser } = useUpdateUser(
      {
        id: editingRow.userId,
        // Inline edits of user fields are shown in the tickets list
        config: { invalidateQueryKeys: [["user"], ["tickets"]] },
      },
      { skip: !editingRow.userId }
    );
    const { mutateAsync: updateTicket } = useUpdateTicket(
      {
        ticketId: editingRow.ticketId,
      },
      { skip: !editingRow.ticketId }
    );

    const { mutate: createTicket } = useCreateTicket();

    const { data: campaignsData } = useGetAllCampaigns({
      eventId: currentEventId,
      page: 1,
      limit: 100,
    });
    const hasAvailableCampaigns = (campaignsData?.data ?? []).some(
      (campaign) => !isContentEmpty(campaign.contentHtml)
    );

    const { data: tagsData, isPending: tagsPending } =
      useGetPeopleTagsBySubtype({
        eventId: currentEventId,
        subtype: "categoryOfUser",
      });

    const getAttendeeTagId = (tagsData) => {
      if (tagsData) {
        return tagsData.find((tag) => tag.title.toLowerCase() === "attendee")
          ?.id;
      }
    };

    const tags = tagsData?.map((tag) => ({
      value: String(tag.id),
      label: tag.title,
    }));
    const { mutate: deleteTicket } = useDeleteTicket();
    const confirmDelete = useDeleteConfirm("ticket", deleteTicket);

    const saveRowAdd = (row) => {
      const { id, firstName, lastName, email, category, ...rest } = row;

      const categoryOfUser = Array.isArray(category)
        ? category
        : category != null
          ? [category]
          : [];

      const attendee = {
        eventId: currentEventId,
        userData: {
          firstName: firstName,
          lastName: lastName,
          email: email,
          categoryOfUser,
          customFields: rest,
        },
      };

      createTicket(attendee);
    };

    const saveRowEdit = async (row) => {
      setEditingRow({ ticketId: row.id, userId: row.userId });
      const originalRow = data.data?.find((r) => r.id === row.id);

      // call mutation to update the ticket
      const changedData = Object.keys(row).reduce((acc, key) => {
        if (row[key] !== originalRow[key]) {
          acc[key] = row[key];
        }
        return acc;
      }, {});

      const userFields = [
        "firstName",
        "lastName",
        // We are moving email to ticket fields,
        // so ticket service will handle it instead of user service
        // CS-740
        // "email",
        "occupation",
        "biography",
      ];
      const updatePromises = [];

      // If changed data is for user data, user should be updated
      if (userFields.some((field) => changedData[field] !== undefined)) {
        const userData = userFields.reduce((acc, field) => {
          if (changedData[field]) {
            acc[field] = changedData[field];
            delete changedData[field];
          }
          return acc;
        }, {});

        if (Object.keys(userData).length > 0) {
          updatePromises.push(updateUser(userData));
        }
      }

      // Category, email and custom fields go in one ticket update so history logs a single entry;
      // email goes through the ticket service to handle an existing user
      const { category, email, ...customFields } = changedData;
      if (category || email || Object.keys(customFields).length > 0) {
        updatePromises.push(
          updateTicket({
            ...(category && { categoryOfUser: category }),
            ...(email && { email }),
            ...(Object.keys(customFields).length > 0 && { customFields }),
          })
        );
      }

      try {
        await Promise.all(updatePromises);
        setEditingRow({ rowId: null, columnClicked: null });
      } catch (err) {
        console.error("Error on inline save:", err);
        throw new Error("Error on inline save");
      }
    };

    // Create a wrapper for setFilter that also cancels any active editing
    const handleFilterChange = (value) => {
      ref.current?.cancelEditing();
      filter.setFilter(value);
    };

    const visibleColumns = columnsData
      ?.filter((column) => column.isVisible === true)
      .map((column) => column.name);

    const tableColumns = [
      {
        accessor: "picture",
        width: "0%",
        editable: false,
        hidden: !visibleColumns?.includes("picture"),
        draggable: true,
        render: (row) => (
          <Group justify="center">
            <Avatar
              src={row?.picture}
              size="md"
              radius="sm"
              alt="avatar"
              color="initials"
              name={`${row?.firstName} ${row?.lastName}`}
            />
          </Group>
        ),
        align: "center",
      },
      {
        accessor: "firstName",
        editable: true,
        sortable: true,
        hidden: !visibleColumns?.includes("firstName"),
        draggable: true,
        inputProps: {
          placeholder: "First name",
        },
        filter: (
          <FilterSearch
            query={filter.data}
            setQuery={handleFilterChange}
            columnName="firstName"
          />
        ),
        filtering: filter?.data?.firstName,
      },
      {
        accessor: "lastName",
        sortable: true,
        editable: true,
        hidden: !visibleColumns?.includes("lastName"),
        draggable: true,
        inputProps: {
          placeholder: "Last name",
        },
        filter: (
          <FilterSearch
            query={filter.data}
            setQuery={handleFilterChange}
            columnName="lastName"
          />
        ),
        filtering: filter?.data?.lastName,
      },
      {
        accessor: "email",
        sortable: true,
        editable: true,
        hidden: !visibleColumns?.includes("email"),
        draggable: true,
        inputProps: {
          placeholder: "Email",
        },
        filter: (
          <FilterSearch
            query={filter.data}
            setQuery={handleFilterChange}
            columnName="email"
          />
        ),
        filtering: filter?.data?.email,
      },
      {
        accessor: "category",
        draggable: true,
        render: (row) => {
          const tags = row?.tags?.filter(
            (tag) => tag.subtype === "categoryOfUser"
          );

          if (tags?.length === 0 || !tags) {
            return (
              <Text c="gray" size="sm">
                No category
              </Text>
            );
          }

          return tags?.map((tag) => (
            <Badge color={tag.color} variant="light" key={tag.id}>
              {tag.title}
            </Badge>
          ));
        },
        editable: true,
        hidden: !visibleColumns?.includes("category"),
        inputProps: {
          placeholder: "Category",
        },
        type: "tags",
        options: tags,
        filter: (
          <CategoryFilter
            query={filter.data}
            setQuery={handleFilterChange}
            tags={tags}
            isPending={tagsPending}
          />
        ),
        filtering: filter?.data?.category,
      },
      {
        accessor: "checkedInAt",
        title: "Checked in",
        width: "0%",
        sortable: true,
        editable: false,
        hidden: !visibleColumns?.includes("checkedInAt"),
        draggable: true,
        render: (row) =>
          row.id !== "temp" && (
            <Group justify="center">
              <TicketSwitch
                ticketId={row.id}
                checked={Boolean(row.checkedInAt)}
                columnName="checkedInAt"
              />
            </Group>
          ),
      },
      ...(customColumns || []).map((column) => {
        return {
          accessor: column.name,
          editable: true,
          hidden: !column.isVisible,
          draggable: true,
          render: (row) => formatCustomFieldValue(row[column.name], column),
          type: FIXED_OPTIONS[column.type] ? "select" : column.type,
          options: FIXED_OPTIONS[column.type] ?? column.options ?? [],
          filter: (
            <FilterSearch
              query={filter.data}
              setQuery={handleFilterChange}
              columnName={column.name}
            />
          ),
          filtering: filter?.data?.[column.name],
        };
      }),
      {
        accessor: "actions",
        textAlign: "right",
        width: 160,
        editable: false,
        render: (row) => (
          <TableActions>
            <TableAction
              label={
                hasAvailableCampaigns
                  ? `Send campaign to ${row.firstName} ${row.lastName}`
                  : "No campaigns available"
              }
              icon={IconMailForward}
              color="violet"
              disabled={!hasAvailableCampaigns}
              onClick={() =>
                openSendCampaignModal({
                  eventId: currentEventId,
                  ticket: row,
                })
              }
            />
            <TableAction
              label={`Print ticket for ${row.firstName} ${row.lastName}`}
              icon={IconPrinter}
              color="cyan"
              onClick={() => handlePrint(row)}
            />
            <EditAction to={`${row.id}`} />
            <DeleteAction onDelete={() => confirmDelete({ id: row.id })} />
          </TableActions>
        ),
      },
    ];

    const { effectiveColumns, resetColumnsOrder } = useDataTableColumns({
      key: `tickets-table-v2-${currentEventId}`,
      columns: tableColumns,
    });

    return (
      <>
        <Group justify="flex-end" pr="xs" my="sm">
          <Tooltip label="Reset column order">
            <ActionIcon
              variant="light"
              color="blue"
              onClick={() => {
                resetColumnsOrder();
              }}
            >
              <IconRefresh />
            </ActionIcon>
          </Tooltip>
        </Group>

        <InlineEditableTable
          ref={ref}
          data={data?.data}
          renderHeader={() => null}
          createEmptyRow={() => ({
            id: "temp",
            firstName: filter.data?.firstName || "",
            lastName: filter.data?.lastName || "",
            email: filter.data?.email || "",
            category: filter.data?.category || getAttendeeTagId(tagsData),
            // Add custom fields to the empty row
            ...customColumns?.reduce((acc, column) => {
              acc[column.name] = filter.data?.[column.name] || "";
              return acc;
            }, {}),
          })}
          validateRow={(row) => {
            const errors = {};

            if (!row.firstName) {
              errors.firstName = "First name is required";
            }

            if (!row.lastName) {
              errors.lastName = "Last name is required";
            }

            if (!row.email) {
              errors.email = "Email is required";
            } else if (!isValidEmail(row.email)) {
              errors.email = "Invalid email format";
            }

            return errors;
          }}
          onRowCreate={(row) => saveRowAdd(row)}
          onRowChange={(row) => saveRowEdit(row)}
          columns={effectiveColumns}
          resizable={true}
          tableProps={{
            storeColumnsKey: `tickets-table-v2-${currentEventId}`,
            pinLastColumn: true,
            page: pagination.data.page,
            onPageChange: (page) =>
              pagination.setPagination({ ...pagination.data, page }),
            sortStatus: sorting.data,
            onSortStatusChange: sorting.setSorting,
            fetching: isPending,
            recordsPerPage: data?.pagination.perPage,
            totalRecords: data?.pagination.total,
          }}
        />
      </>
    );
  }
);

function FilterSearch({ query, setQuery, columnName }) {
  // Transform camel case to readable text
  const camelToReadableColumnName = columnName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase();
  return (
    <TextInput
      label="Tickets"
      description={`Show tickets whose ${camelToReadableColumnName} includes the specified text`}
      placeholder="Search tickets..."
      leftSection={<IconSearch size={16} />}
      rightSection={
        <ActionIcon
          size="sm"
          variant="transparent"
          c="dimmed"
          onClick={() => setQuery(null)}
        >
          <IconX size={14} />
        </ActionIcon>
      }
      value={query?.[columnName] || ""}
      onChange={(e) => {
        if (e.currentTarget.value === "") {
          setQuery(null);
          return;
        }
        setQuery({ [columnName]: e.currentTarget.value });
      }}
    />
  );
}

function CategoryFilter({ query, setQuery, tags, isPending }) {
  if (isPending) {
    <Text>Loading tags...</Text>;
  }

  return (
    <Select
      label="Category"
      description="Filter tickets by category"
      placeholder="Filter by category"
      data={tags}
      value={query?.category}
      onChange={(value) => {
        setQuery({ category: value });
      }}
      onClear={() => setQuery(null)}
      clearable
    />
  );
}

export default TicketsTable;
